import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { FadeIn, MaskedLine, Reveal } from "../components/Reveal";
import { Counter } from "../components/Counter";
import { ProjectCard } from "../components/ProjectCard";
import { ProjectMarquee } from "../components/Marquee";
import { TimelineSection } from "../components/Timeline";
import { RecognitionSection } from "../components/Recognition";
import { SEO } from "../components/SEO";
import { HERO_IMAGE, projects, stats, timeline } from "../data/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy-deep">
      <motion.div style={{ y: imgY }} className="absolute inset-0 will-change-transform">
        <img src={HERO_IMAGE} alt="Contemporary architecture by Parsn Group" className="w-full h-full object-cover scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 via-navy-deep/35 to-navy-deep/20" />
      </motion.div>
      <motion.div style={{ y: textY }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-16 sm:pb-24 pt-40">
        <div aria-hidden="true" className="absolute -inset-x-2 inset-y-24 sm:inset-x-6 sm:inset-y-28 -z-10 bg-navy-deep/[0.01] rounded-[2rem]" />
        <FadeIn delay={0.15}>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-white/70 mb-6">
            <span className="w-10 h-px bg-brand" aria-hidden="true" /> Est. 1971 · Mumbai
          </p>
        </FadeIn>
        <h1 className="font-serif font-light text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
          <MaskedLine delay={0.35}>Building trust since 1971.</MaskedLine>
          <MaskedLine delay={0.55}>Delivering homes with <span className="italic text-brand">love.</span></MaskedLine>
        </h1>
        <FadeIn delay={1.0}>
          <p className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed">
            From a legacy of construction specialization to redevelopment across East Mumbai —
            five decades of meaningfully delivered construction.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/projects"
              data-testid="hero-cta-projects"
              className="inline-flex items-center gap-2 bg-brand text-white text-sm font-semibold px-7 py-4 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#e0671a]"
            >
              Explore ongoing projects <ArrowRight size={16} />
            </Link>
            <a
              href="#track-record"
              data-testid="hero-cta-track-record"
              className="inline-flex items-center gap-2 border border-white/30 text-white text-sm font-semibold px-7 py-4 transition-[background-color,border-color] duration-300 hover:bg-white/10 hover:border-white/60"
            >
              Our track record
            </a>
          </div>
        </FadeIn>
      </motion.div>
    </section>
  );
};

const Stats = () => (
  <section data-testid="stats-section" className="bg-white">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
      <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-gray-200">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="border-b border-r border-gray-200 p-6 sm:p-10">
            <p className="font-serif text-4xl sm:text-5xl text-navy tracking-tight">
              <Counter value={s.value} testid={`stat-counter-${i}`} />
            </p>
            <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.15em] text-gray-500">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const HOME_PROJECT_ORDER = ["bhagyalakshmi", "shiv-vaibhav", "pancharatna", "sharada", "avighna"];

const ProjectsPreview = () => {
  const scrollRef = useRef(null);
  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 420, behavior: "smooth" });

  return (
    <section data-testid="projects-preview-section" className="bg-white pt-16 sm:pt-24 pb-24 sm:pb-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12 sm:mb-16">
          <div>
            <p className="overline-label mb-4">Ongoing projects</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-ink tracking-tight max-w-xl leading-tight">
              Addresses rising across Kurla and Chembur
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="projects-prev-button" onClick={() => scrollBy(-1)} aria-label="Scroll projects back" className="w-11 h-11 border border-gray-300 text-navy flex items-center justify-center transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white">
              <ChevronRight size={18} className="rotate-180" />
            </button>
            <button data-testid="projects-next-button" onClick={() => scrollBy(1)} aria-label="Scroll projects forward" className="w-11 h-11 border border-gray-300 text-navy flex items-center justify-center transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white">
              <ChevronRight size={18} />
            </button>
            <Link
              to="/projects"
              data-testid="projects-view-all-link"
              className="ml-2 inline-flex items-center gap-2 text-sm font-semibold text-navy border-b-2 border-brand pb-1 transition-colors duration-300 hover:text-brand"
            >
              View all <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
      <Reveal>
        <div
          ref={scrollRef}
          data-testid="projects-scroller"
          className="bleed-scroll flex gap-6 sm:gap-8 overflow-x-auto no-scrollbar snap-x snap-mandatory pl-5 sm:pl-8 pr-5 sm:pr-8 pb-2 items-stretch"
        >
          {HOME_PROJECT_ORDER.map((slug) => projects.find((p) => p.slug === slug)).filter(Boolean).map((p) => (
            <div key={p.slug} className="snap-start shrink-0 w-[85vw] sm:w-[420px] flex">
              <ProjectCard project={p} index={0} />
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const chapters = [
  {
    num: "01",
    title: "Foundations",
    text: "In 1971, Mr. Prabhakar Ganapatrao Pathare began with piling and foundation work — the unseen strength beneath Bombay's institutions, from Bombay Port Trust to K.E.M. Hospital, Pune.",
  },
  {
    num: "02",
    title: "Construction",
    text: "The practice grew into full construction — hospitals, schools, churches and residences across Mumbai, Pune, Chennai and Saudi Arabia, delivered time-bound and without compromise.",
  },
  {
    num: "03",
    title: "Redevelopment",
    text: "Rajan Pathare brought Parsn into redevelopment in 2007. The legacy continues with ongoing redevelopments in various parts of Mumbai including Kurla and Chembur, delivering renewed homes and dreams to home buyers in modern homes.",
  },
];

const Story = () => (
  <section data-testid="story-section" className="bg-paper border-y border-gray-200 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mb-16 sm:mb-24">
        <p className="overline-label mb-4">Since 1971</p>
        <h2 className="font-serif text-3xl sm:text-5xl text-ink tracking-tight leading-tight max-w-2xl">
          A track record built over five decades
        </h2>
      </Reveal>
      <div className="flex flex-col gap-16 sm:gap-24">
        {chapters.map((c, i) => (
          <Reveal key={c.num} className={`grid md:grid-cols-12 gap-6 md:gap-10 items-start ${i % 2 ? "" : ""}`}>
            <span className="font-serif text-6xl sm:text-8xl text-navy/10 leading-none md:col-span-3 select-none">
              {c.num}
            </span>
            <div className="md:col-span-9 lg:col-span-7">
              <h3 className="font-serif text-2xl sm:text-4xl text-navy tracking-tight">{c.title}</h3>
              <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-20 sm:mt-28 grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
        {[
          { label: "Vision", text: "To be a world-class creator of sustainable and quality infrastructure and construction projects." },
          { label: "Mission", text: "To achieve completion of projects in a time-bound manner without compromising on quality and benefits to the stakeholders." },
          { label: "Values", text: "Client centric. Commitment. Excellence." },
        ].map((v) => (
          <div key={v.label} className="bg-white p-8 sm:p-10">
            <p className="overline-label mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-brand" aria-hidden="true" /> {v.label}
            </p>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{v.text}</p>
          </div>
        ))}
      </Reveal>
    </div>
  </section>
);

const CTA = () => (
  <section data-testid="cta-section" className="bg-paper border-t border-gray-200 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-3xl">
        <p className="overline-label mb-4">Begin a conversation</p>
        <h2 className="font-serif text-3xl sm:text-5xl text-ink tracking-tight leading-tight">
          A home, a partnership, or a redevelopment — <span className="italic text-navy">let's talk.</span>
        </h2>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="cta-contact-button"
            className="inline-flex items-center gap-2 bg-brand text-white text-sm font-semibold px-7 py-4 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#e0671a]"
          >
            Send an enquiry <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const HOME_TIMELINE_ERAS = ["Present Day", "2005 – 20", "1990 – 2000", "1975 – 76", "1973 – 74", "1972 – 73", "1971 – 72"];

export default function Home() {
  return (
    <main>
      <SEO
        title="Parsn Group — Building Trust Since 1971"
        description="Mumbai's construction and redevelopment house since 1971. Ongoing MahaRERA-registered projects across Kurla and Chembur — 1 RK, 1, 2 & 2.5 BHK homes."
        path="/"
      />
      <Hero />
      <Stats />
      <ProjectMarquee />
      <ProjectsPreview />
      <Story />
      <TimelineSection
        items={HOME_TIMELINE_ERAS.map((era) => timeline.find((t) => t.era === era)).filter(Boolean)}
        exploreLink
        projectsLink
      />
      <RecognitionSection />
      <CTA />
    </main>
  );
}
